/**
 * PageSet action: writes or clears `brandedMedPreference` in `purchaseSurveyAnswers`.
 * Payment medication SKU resolution matches plan tier via `getRecommendedMedSkuFromMedicationPreferenceAnswers`.
 */
import type { SurveyAnswersState as MergedSurveyAnswers } from "@utils/redux/slices/surveyAnswers";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import {
  getDefaultEligibilityOptions,
  getEligibleMedProducts,
  MED_SKU,
} from "src/utils/medEligibility";
import { getRecommendedMedSkuFromMedicationPreferenceAnswers } from "src/utils/getRecommendedMedSkuFromMedicationPreferenceAnswers";
import { saveSurveyAnswers } from "src/utils/redux/slices/userData";
import {
  isBrandedAsyncEnabled,
  isGlpMedPreferenceWegovyPillAnswer,
  isWegovyPillPaidTraffic,
  isZepboundFeatureEnabled,
} from "src/utils/userSegment/features";
import type { ActionHandler, ActionHandlerParams } from "..";

/** Values aligned with healthcare `DrugPreferenceEnumProto` */
export type BrandedMedPreferenceValue =
  | "WEGOVY_PEN"
  | "WEGOVY_PILL"
  | "ZEPBOUND_SINGLE_USE_VIAL";

declare module "@utils/redux/slices/surveyAnswers" {
  interface SurveyAnswersState {
    brandedMedPreference?: BrandedMedPreferenceValue;
  }
}

function medSkuToBrandedPreference(
  sku: MED_SKU | undefined
): BrandedMedPreferenceValue | undefined {
  if (!sku) {
    return undefined;
  }
  switch (sku) {
    case MED_SKU.BRANDED:
      return "WEGOVY_PEN";
    case MED_SKU.BRANDED_WEGOVY_PILL:
      return "WEGOVY_PILL";
    case MED_SKU.BRANDED_ZEPBOUND_VIAL:
      return "ZEPBOUND_SINGLE_USE_VIAL";
    default:
      return undefined;
  }
}

export function resolveBrandedMedPreference(
  answers: MergedSurveyAnswers
): BrandedMedPreferenceValue | undefined {
  if (answers.medicationPreference) {
    const eligibleProducts = getEligibleMedProducts(
      getDefaultEligibilityOptions()
    );
    const skuFromMedicationPreference =
      getRecommendedMedSkuFromMedicationPreferenceAnswers(
        eligibleProducts,
        answers
      );
    return medSkuToBrandedPreference(skuFromMedicationPreference) ?? undefined;
  }

  const glpAnswer = answers.glpMedPreference?.[0] as string | undefined;
  if (glpAnswer === "tirzepatide") {
    return "ZEPBOUND_SINGLE_USE_VIAL";
  }
  if (isGlpMedPreferenceWegovyPillAnswer(glpAnswer)) {
    return "WEGOVY_PILL";
  }
  if (glpAnswer === "semaglutide") {
    return "WEGOVY_PEN";
  }

  if (isWegovyPillPaidTraffic()) {
    return "WEGOVY_PILL";
  }

  if (
    isZepboundFeatureEnabled() &&
    answers.zepboundMedicationPreference?.[0] === "no"
  ) {
    return "ZEPBOUND_SINGLE_USE_VIAL";
  }

  if (
    isZepboundFeatureEnabled() &&
    glpAnswer !== "openToOptions" &&
    glpAnswer !== "recommendation" &&
    answers.zepboundMedicationPreference?.[0] !== "yes"
  ) {
    return "ZEPBOUND_SINGLE_USE_VIAL";
  }

  return undefined;
}

export const persistBrandedMedPreference: ActionHandler = async ({
  store,
}: ActionHandlerParams) => {
  if (!isBrandedAsyncEnabled()) {
    return;
  }

  const state = store.getState();
  const value = resolveBrandedMedPreference(getSurveyAnswers(state));
  const { purchaseSurveyAnswers = {} } = state.userData;

  if (value === undefined) {
    if (purchaseSurveyAnswers.brandedMedPreference === undefined) {
      return;
    }

    const cleared: MergedSurveyAnswers = { ...purchaseSurveyAnswers };
    delete cleared.brandedMedPreference;

    await store.dispatch(
      saveSurveyAnswers(cleared, {
        surveyNameSpace: "purchaseSurveyAnswers",
        overwrite: true,
      })
    );

    return;
  }

  await store.dispatch(
    saveSurveyAnswers(
      { brandedMedPreference: value },
      { surveyNameSpace: "purchaseSurveyAnswers", overwrite: false }
    )
  );
};
